import './search-nav.web.css';

import { useEffect, useMemo, useState } from 'react';

import {
  SidebarDatabaseIcon,
  SidebarPageBlankIcon,
} from '@/components/layout/SidebarIcons.web';
import { getRecentPages } from '@/lib/navigation/recent-pages';
import { useContentSearch } from '@/lib/search/use-content-search';
import type { SearchContainerResult } from '@/lib/search/types';

export type SearchPickerSelection = SearchContainerResult;

type SearchPickerProps = {
  query: string;
  selectedIndex: number;
  onSelect: (item: SearchPickerSelection) => void;
  scopeRootIds?: string[];
  showRecentsWhenEmpty?: boolean;
  heading?: string;
  limit?: number;
};

function ContainerIcon({ type }: { type: SearchContainerResult['containerType'] }) {
  return type === 'database' ? <SidebarDatabaseIcon /> : <SidebarPageBlankIcon />;
}

function recentToSelection(page: {
  id: string;
  title: string;
  type: 'page' | 'database';
}): SearchPickerSelection {
  return {
    containerId: page.id,
    containerType: page.type,
    title: page.title,
    snippet: '',
    matchCount: 1,
    matchedVia: [],
    topBlockId: page.id,
    score: 0,
  };
}

export function SearchPicker({
  query,
  selectedIndex,
  onSelect,
  scopeRootIds,
  showRecentsWhenEmpty = true,
  heading,
  limit = 20,
}: SearchPickerProps) {
  const trimmed = query.trim();
  const { grouped, loading, error } = useContentSearch(trimmed, { scopeRootIds, limit });
  const [recents, setRecents] = useState<SearchPickerSelection[]>([]);

  useEffect(() => {
    if (!showRecentsWhenEmpty || trimmed) {
      setRecents([]);
      return;
    }
    setRecents(getRecentPages().slice(0, 8).map(recentToSelection));
  }, [showRecentsWhenEmpty, trimmed]);

  const items = useMemo(() => {
    if (trimmed) {
      return grouped;
    }
    return showRecentsWhenEmpty ? recents : [];
  }, [grouped, recents, showRecentsWhenEmpty, trimmed]);

  const safeIndex = items.length > 0 ? selectedIndex % items.length : 0;
  const listHeading =
    heading ??
    (trimmed
      ? loading
        ? 'Searching…'
        : 'Results'
      : showRecentsWhenEmpty
        ? 'Recent'
        : '');

  return (
    <div className="couplespace-link-picker search-picker-results">
      <div className="couplespace-link-picker-section">
        <div className="couplespace-link-picker-heading">{listHeading}</div>
        {error ? (
          <div className="couplespace-link-picker-empty">{error}</div>
        ) : items.length === 0 ? (
          <div className="couplespace-link-picker-empty">
            {loading ? 'Searching…' : trimmed ? 'No matching pages or databases' : 'No recent pages'}
          </div>
        ) : (
          <ul className="couplespace-link-picker-list">
            {items.map((item, index) => (
              <li key={item.containerId}>
                <button
                  className={
                    index === safeIndex
                      ? 'couplespace-link-picker-item couplespace-link-picker-item-selected'
                      : 'couplespace-link-picker-item'
                  }
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    onSelect(item);
                  }}
                >
                  <span className="couplespace-link-picker-item-icon">
                    <ContainerIcon type={item.containerType} />
                  </span>
                  <span className="couplespace-link-picker-item-body">
                    <span className="couplespace-link-picker-item-title">{item.title}</span>
                    {item.snippet ? (
                      <span className="couplespace-link-picker-item-breadcrumb">{item.snippet}</span>
                    ) : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
